import React from 'react';
import { Link } from 'react-router-dom';
import { FlaskConical, Cog, Factory, Filter, ArrowRight } from 'lucide-react';

// Customer-facing service cards, shared by the Partner hub and the industry pages.
export const SERVICE_CARDS = [
  {
    key: 'crdmo', path: '/partner/crdmo', icon: FlaskConical, tag: 'R&D', accent: 'from-orange-500 to-orange-600',
    title: 'Contract Research (CRDMO)', subtitle: 'Feasibility studies, route scouting & early-stage R&D',
    desc: 'Literature reviews, retrosynthesis, lab-scale synthesis, and analytical characterization for emerging molecules.',
  },
  {
    key: 'cdmo', path: '/partner/cdmo', icon: Cog, tag: 'SCALE-UP', accent: 'from-teal-500 to-teal-600',
    title: 'Contract Development & Manufacturing (CDMO)', subtitle: 'Scale-up, process optimization & pilot production',
    desc: 'Process development, HAZOP evaluation, pilot-scale batches, and regulatory-ready technology transfer.',
  },
  {
    key: 'cmo', path: '/partner/cmo', icon: Factory, tag: 'SUPPLY', accent: 'from-orange-500 to-orange-600',
    title: 'Contract Manufacturing (CMO)', subtitle: 'Commercial-scale production & supply chain',
    desc: 'Reliable kg to MT-scale supply with full COA, quality audits, and long-term framework contracts.',
  },
  {
    key: 'purification', path: '/partner/purification', icon: Filter, tag: 'PURITY', accent: 'from-teal-500 to-teal-600',
    title: 'Purification Services', subtitle: 'High-purity material processing & metal impurity removal',
    desc: 'Multi-pass sublimation, chromatography, zone refining - reaching ultra-high purity with ICP-MS / GD-MS verification.',
  },
];

export const ServiceCards = ({ keys, industry, columns = 'md:grid-cols-2' }) => {
  const cards = keys ? SERVICE_CARDS.filter((c) => keys.includes(c.key)) : SERVICE_CARDS;
  const q = industry ? `?industry=${encodeURIComponent(industry)}` : '';
  return (
    <div className={`grid ${columns} gap-5 md:gap-6`} data-testid="service-cards">
      {cards.map((card) => {
        const Icon = card.icon;
        return (
          <Link key={card.key} to={card.path + q} data-testid={`service-card-${card.key}`}
            className="group relative flex flex-col rounded-xl border border-gray-200 bg-white p-6 md:p-7 hover:border-orange-400 hover:shadow-xl transition-all duration-300 overflow-hidden">
            <div className={`absolute top-0 left-0 right-0 h-1 bg-gradient-to-r ${card.accent}`} />
            <div className="flex items-center justify-between mb-5">
              <div className={`w-12 h-12 rounded-lg bg-gradient-to-br ${card.accent} flex items-center justify-center shadow-sm`}>
                <Icon className="text-white" size={22} />
              </div>
              <span className="text-[10px] font-bold tracking-[0.15em] text-gray-400 uppercase">{card.tag}</span>
            </div>
            <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-1.5 leading-snug group-hover:text-orange-600 transition-colors">{card.title}</h3>
            <p className="text-sm font-semibold text-teal-700 mb-3">{card.subtitle}</p>
            <p className="text-sm text-gray-600 leading-relaxed mb-5 flex-1">{card.desc}</p>
            <div className="inline-flex items-center gap-1.5 text-sm font-semibold text-orange-600 group-hover:gap-3 transition-all">
              Start inquiry <ArrowRight size={16} />
            </div>
          </Link>
        );
      })}
    </div>
  );
};

export const IndustryServices = ({ industry, label }) => (
  <section className="py-12 md:py-20 bg-white" data-testid="industry-services">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="text-center mb-8 md:mb-12">
        <div className="text-xs font-bold tracking-widest text-orange-600 uppercase mb-2">Request services</div>
        <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-3">How LAMKO can work with you on {label}</h2>
        <p className="text-base md:text-lg text-gray-600 max-w-3xl mx-auto">
          From a first feasibility study to commercial supply, each service has its own enquiry form. Your request is routed to the project manager for {label}.
        </p>
      </div>
      <ServiceCards industry={industry} />
    </div>
  </section>
);

export default ServiceCards;
